import axios, {
  AxiosError,
  AxiosHeaders,
} from 'axios';
import { clearLegacyAuthState } from '../auth/session.ts';

const UNSAFE_METHODS = new Set(['post', 'put', 'patch', 'delete']);
const CSRF_EXEMPT_PATHS = new Set(['/api/auth/login']);
const BLOCKED_MACHINE_PATHS = new Set(['/api/data/sync']);

export class FrontendAuthError extends Error {
  code: string;
  status?: number;

  constructor(code: string, message: string, status?: number) {
    super(message);
    this.name = 'FrontendAuthError';
    this.code = code;
    this.status = status;
  }
}

function pathname(url?: string): string {
  if (!url) return '';
  try {
    return new URL(url, 'http://frontend.local').pathname;
  } catch {
    return url.split('?')[0];
  }
}

export function readCookieExact(cookieHeader: string, cookieName: string): string | null {
  for (const part of cookieHeader.split(';')) {
    const separator = part.indexOf('=');
    if (separator < 0) continue;
    const name = part.slice(0, separator).trim();
    if (name !== cookieName) continue;
    const value = part.slice(separator + 1).trim();
    try {
      return decodeURIComponent(value);
    } catch {
      return null;
    }
  }
  return null;
}

export function readCsrfCookie(): string | null {
  if (typeof document === 'undefined') return null;
  return readCookieExact(document.cookie, 'tz_csrf');
}

export function isUnsafeMethod(method?: string): boolean {
  return UNSAFE_METHODS.has((method || 'get').toLowerCase());
}

export function getHttpStatus(error: unknown): number | undefined {
  if (error instanceof FrontendAuthError) return error.status;
  if (axios.isAxiosError(error)) return error.response?.status;
  return undefined;
}

function safeValidationMessage(error: AxiosError): string {
  const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
  if (typeof detail === 'string' && detail.length <= 240) return detail;
  return '请求参数有误，请检查后重试';
}

export function getApiErrorMessage(error: unknown, fallback = '请求失败，请稍后重试'): string {
  if (error instanceof FrontendAuthError) return error.message;
  if (!axios.isAxiosError(error)) return fallback;
  const status = error.response?.status;
  if (status === 401) return '登录状态已失效，请重新登录';
  if (status === 403) return '权限不足或安全校验失败';
  if (status === 422) return safeValidationMessage(error);
  if (status === 503) return '系统繁忙，请稍后重试';
  if (!error.response) return '网络连接失败，请检查网络后重试';
  return fallback;
}

export const apiClient = axios.create({
  baseURL: '',
  withCredentials: true,
  timeout: 15_000,
  headers: {
    Accept: 'application/json',
  },
});

apiClient.interceptors.request.use((request) => {
  const path = pathname(request.url);
  if (BLOCKED_MACHINE_PATHS.has(path)) {
    throw new FrontendAuthError('machine_auth_blocked', '机器身份尚未配置');
  }


  if (isUnsafeMethod(request.method) && !CSRF_EXEMPT_PATHS.has(path)) {
    const csrfToken = readCsrfCookie();
    if (!csrfToken) {
      throw new FrontendAuthError(
        'csrf_cookie_missing',
        '安全校验信息缺失，请重新确认登录状态',
        403,
      );
    }
    if (!(request.headers instanceof AxiosHeaders)) {
      request.headers = new AxiosHeaders(request.headers);
    }
    request.headers.set('X-CSRF-Token', csrfToken);
  }
  return request;
});

apiClient.interceptors.response.use(
  (response) => response.data,
  (error: unknown) => {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      clearLegacyAuthState();
    }
    return Promise.reject(error);
  },
);
